import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { SnapmonLogo } from '../components/SnapmonLogo';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { ThemeToggle } from '../components/ThemeToggle';
import { adminApi } from '../api/admin';
import { useAuthStore } from '../auth/store';
import type { ApiError } from '../api/client';

const Schema = z.object({
  username: z.string().min(3, 'Username too short'),
  password: z.string().min(1, 'Password required'),
});
type FormValues = z.infer<typeof Schema>;

export function LoginScreen() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [showPw, setShowPw] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: { username: '', password: '' },
  });

  const loginMutation = useMutation({
    mutationFn: (v: FormValues) => adminApi.login(v.username, v.password),
    onSuccess: (res) => {
      setSession(res.token, res.admin);
      navigate('/', { replace: true });
    },
    onError: (err: ApiError) => setFormError(err?.message ?? 'Login failed'),
  });

  const onSubmit = (v: FormValues) => {
    const parsed = Schema.safeParse(v);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    loginMutation.mutate(parsed.data);
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 28px' }}>
        <SnapmonLogo />
        <ThemeToggle />
      </div>
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            width: '100%',
            maxWidth: 380,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            padding: 28,
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div>
            <Pill tone="brand" size="md">Internal · staff only</Pill>
            <h1
              style={{
                margin: '12px 0 4px',
                fontFamily: 'var(--font-display)',
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: '-0.02em',
              }}
            >
              Snapmon Admin
            </h1>
            <p style={{ color: 'var(--ink-3)', fontSize: 13, margin: 0 }}>
              Sign in with your staff credentials.
            </p>
          </div>

          <Field label="Username" error={errors.username?.message}>
            <input
              {...register('username')}
              autoFocus
              autoComplete="username"
              placeholder="harun"
              style={inputStyle}
            />
          </Field>

          <Field
            label="Password"
            error={errors.password?.message}
            right={
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  padding: '4px 6px',
                }}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            }
          >
            <input
              {...register('password')}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              style={{
                ...inputStyle,
                fontFamily: showPw ? 'var(--font-body)' : 'var(--font-mono)',
                letterSpacing: showPw ? 'normal' : '0.18em',
              }}
            />
          </Field>

          {formError && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--bad)',
                background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
                border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
                padding: '8px 12px',
                borderRadius: 'var(--r-sm)',
              }}
            >
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--brand)',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 'var(--r-md)',
              fontWeight: 600,
              fontSize: 14,
              cursor: loginMutation.isPending ? 'progress' : 'pointer',
              opacity: loginMutation.isPending ? 0.85 : 1,
            }}
          >
            {loginMutation.isPending ? (
              <>
                <span className="snapmon-spinner" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <Icon.Arrow />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  flex: 1,
  minWidth: 0,
  border: 0,
  background: 'transparent',
  outline: 'none',
  fontSize: 14,
  color: 'var(--ink)',
} as const;

function Field({
  label,
  children,
  right,
  error,
}: {
  label: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  error?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-elev)',
          border: '1px solid ' + (error ? 'var(--bad)' : 'var(--line-strong)'),
          borderRadius: 'var(--r-md)',
          padding: '10px 12px',
        }}
      >
        {children}
        {right}
      </div>
      {error && <span style={{ fontSize: 11, color: 'var(--bad)' }}>{error}</span>}
    </label>
  );
}
